import React from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useFeature } from 'flagged';
import PropTypes from 'prop-types';
import Avatar from '@/components/common/Avatar';
import BackButton from '@/components/common/BackButton';
import LabelChips from '@/components/common/LabelChips';
import LastInteraction from '@/components/common/LastInteraction';
import Loader from '@/components/common/Loader';
import ViewFieldsColumn from '@/components/common/ViewFieldsColumn';
import ConstituentType from '@/constants/ConstituentType';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import FeatureFlags from '@/constants/system/FeatureFlags';
import useMobileView from '@/hooks/ui/useMobileView';
import {
  ConcernsIcon,
  EmailIcon,
  ProhibitionsIcon,
  WebIcon,
  WinningIcon,
} from '@/lib/configs/donor-icons';
import usePreselectedLabels from '@/lib/hooks/swr/usePreselectedLabels';
import translate from '@/lib/intl/translate';
import {
  IWaveScoreEvaluation,
  useConstituentIWaveData,
} from '@/modules/IWaveIntegration';
import {
  getPrimaryEmail,
  getPrimaryPhoneObject,
  isPersonConstituentType,
} from '@/utils/constituentUtils';
import getFullName from '@/utils/getFullName';
import getInitials from '@/utils/getInitials';
import { ConstituentPropType } from '../configs/constituentTypes';
import ConstituentInfoAssigned from './ConstituentInfoAssigned';
import ConstituentInfoAttributes from './ConstituentInfoAttributes';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import ConstituentLifecycleBoardList from './ConstituentLifecycleBoardList';
import ConstituentMenu from './ConstituentMenu';
import ConstituentPhoneLinkAndIcon from './ConstituentPhoneLinkAndIcon';

const AVATAR_MOB_SIZE = '64px';
const AVATAR_DESKTOP_SIZE = '90px';

const CONSTITUENT_NOTES_CONFIG = [
  {
    key: 'NOTE_1',
    icon: WinningIcon,
    label: translate('CONSTITUENTS_NOTE_PLACEHOLDER_1'),
    path: 'note1',
    editField: 'note_1',
    noDataLabel: translate('CONSTITUENTS_NOTE_PLACEHOLDER_1'),
  },
  {
    key: 'NOTE_2',
    icon: ConcernsIcon,
    label: translate('CONSTITUENTS_NOTE_PLACEHOLDER_2'),
    path: 'note2',
    editField: 'note_2',
    noDataLabel: translate('CONSTITUENTS_NOTE_PLACEHOLDER_2'),
  },
  {
    key: 'NOTE_3',
    icon: ProhibitionsIcon,
    label: translate('CONSTITUENTS_NOTE_PLACEHOLDER_3'),
    path: 'note3',
    editField: 'note_3',
    noDataLabel: translate('CONSTITUENTS_NOTE_PLACEHOLDER_3'),
  },
];

// Info Section for users and orgs (displayed on the left in the desktop mode)
function ConstituentInfoSection({
  data,
  dataFetching,
  dataLoaded,
  isEditable,
  isNewConstituent,
}) {
  const hasIsDeceased = useFeature(FeatureFlags.IS_DECEASED);
  const hasIWave = useFeature(FeatureFlags.IWAVE);

  const history = useHistory();
  const {
    avatar,
    constituentId,
    displayName,
    emails,
    lastInteractionDate,
    orgName = '',
    phones,
    prefix,
    website,
  } = data;

  const primaryPhone = getPrimaryPhoneObject(phones);
  const primaryEmail = getPrimaryEmail(emails);

  const name = React.useMemo(
    () =>
      isPersonConstituentType(data)
        ? [prefix, displayName].filter(Boolean).join(' ')
        : displayName || translate('CONSTITUENT_NAME_LABEL'),
    [displayName],
  );

  const isMobile = useMobileView();

  const initials = React.useMemo(
    () =>
      getInitials(orgName || getFullName(data), isPersonConstituentType(data)),
    [data],
  );

  const avatarSize = React.useMemo(
    () => (isMobile ? AVATAR_MOB_SIZE : AVATAR_DESKTOP_SIZE),
    [isMobile],
  );

  const { data: labels } = usePreselectedLabels(data?.labelIds?.tags);
  const { iwaveProfile } = useConstituentIWaveData(constituentId);

  const onNoDataLabelClick = (key) => {
    const path = constituentId
      ? `${generatePath(AuthRoutes.CONSTITUENT_EDIT, {
          constituentId,
        })}#${key}`
      : `${generatePath(AuthRoutes.CONSTITUENT_NEW, {
          constituentType: isPersonConstituentType(data)
            ? ConstituentType.PERSON
            : ConstituentType.ORG,
        })}#${key}`;

    return history.push(path);
  };

  return (
    <div data-testid="constituent-info-section">
      <div className="flex flex-col gap-6">
        <div className="flex justify-between">
          <BackButton fallback={{ pathname: AuthRoutes.CONSTITUENTS }} />
          <ConstituentMenu constituentId={data?.constituentId} />
        </div>
        <div className="flex flex-col justify-between">
          {dataFetching ? (
            <Loader size={avatarSize} />
          ) : (
            <Avatar size={avatarSize} src={avatar}>
              {initials}
            </Avatar>
          )}
          <div className="flex-grow space-y-2 overflow-hidden pt-3 pb-4">
            <Typography variant="h5">{name}</Typography>
            <div className="text-sm font-normal text-grey-900">
              {data?.pronouns && <span className="pr-3">{data.pronouns}</span>}
              {data?.preferredFirstName && (
                <span>
                  {translate('DONOR_PREFERS')}: {data.preferredFirstName}
                </span>
              )}
            </div>
            {hasIsDeceased && data.isDeceased && (
              <div className="text-sm font-normal text-grey-900">
                {translate('DECEASED_LABEL')}
              </div>
            )}
            <LastInteraction date={lastInteractionDate} variant="body2" />
          </div>
        </div>
        <ConstituentInfoAttributes constituent={data} />
        {!!labels?.length && <LabelChips labels={labels} />}
        {!!(primaryPhone || primaryEmail || website) && (
          <div>
            <h3 className="pb-2 type-h3">{translate('DONOR_KEY_INFO')}</h3>
            <div className="flex flex-col gap-2">
              {primaryPhone && (
                <ConstituentPhoneLinkAndIcon
                  className="flex"
                  phoneObject={primaryPhone}
                />
              )}
              {primaryEmail && (
                <div className="flex">
                  {EmailIcon}
                  <Link className="ml-4" href={`mailto:${primaryEmail}`}>
                    {primaryEmail}
                  </Link>
                </div>
              )}
              {website && (
                <div className="flex">
                  {WebIcon}
                  <Link className="ml-4" href={website}>
                    {website}
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
        {dataLoaded && (
          <ViewFieldsColumn
            config={CONSTITUENT_NOTES_CONFIG}
            className="rounded-lg bg-grey-100 p-4"
            data={data}
            onNoDataLabelClick={onNoDataLabelClick}
            title={translate('DONOR_KEY_NOTES')}
            showNoDataLabel={isEditable}
          />
        )}
        {!isNewConstituent && hasIWave && iwaveProfile?.score && (
          <div>
            <h4 className="type-h3">
              {translate('IWAVE_SCORE_BREAKDOWN_SCORE_HEADER')}
            </h4>
            <p>
              <IWaveScoreEvaluation score={iwaveProfile.score} />
            </p>
          </div>
        )}
        {!isNewConstituent && (
          <div>
            <ConstituentLifecycleBoardList
              constituentId={data?.constituentId}
            />
            <div className="pt-6">
              <ConstituentInfoAssigned
                constituent={data}
                dataFetching={dataFetching}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

ConstituentInfoSection.propTypes = {
  data: ConstituentPropType,
  dataLoaded: PropTypes.bool.isRequired,
  dataFetching: PropTypes.bool.isRequired,
  isEditable: PropTypes.bool,
  isNewConstituent: PropTypes.bool,
};

ConstituentInfoSection.defaultProps = {
  data: {},
  isNewConstituent: false,
  isEditable: false,
};

export default ConstituentInfoSection;
